const K_LONG_INACTIVE_MS = 10 * 60 * 1000;

export interface IAuthWakeRefreshService {
  start(): void;
  dispose(): void;
}

export const AUTH_WAKE_REFRESH_SERVICE = Symbol.for(
  "Campfire.AuthWakeRefreshService",
);

/**
 * Bridges browser focus/visibility events to the headless auth model.
 *
 * Unity reference: `UserInactivityTrackerModel.OnApplicationFocus`, which
 * calls `IAuthModel.EnsureFreshTokens` when the mobile app wakes.
 */
export class AuthWakeRefreshService implements IAuthWakeRefreshService {
  private readonly m_refreshAuthTokens: () => Promise<void>;
  private readonly m_inactiveThresholdMs: number;
  private m_inactiveAtMs: number | null = null;
  private m_isStarted = false;

  constructor(
    refreshAuthTokens: () => Promise<void>,
    inactiveThresholdMs = K_LONG_INACTIVE_MS,
  ) {
    this.m_refreshAuthTokens = refreshAuthTokens;
    this.m_inactiveThresholdMs = inactiveThresholdMs;
  }

  start(): void {
    if (this.m_isStarted) return;
    this.m_isStarted = true;

    if (document.visibilityState !== "visible") {
      this.markInactive();
    }
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("blur", this.handleBlur);
    window.addEventListener("focus", this.handleFocus);
  }

  dispose(): void {
    if (!this.m_isStarted) return;
    this.m_isStarted = false;

    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("blur", this.handleBlur);
    window.removeEventListener("focus", this.handleFocus);
    this.m_inactiveAtMs = null;
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      this.markInactive();
      return;
    }
    this.handleResume();
  };

  private readonly handleBlur = (): void => {
    this.markInactive();
  };

  private readonly handleFocus = (): void => {
    if (document.visibilityState === "visible") {
      this.handleResume();
    }
  };

  private markInactive(): void {
    this.m_inactiveAtMs ??= Date.now();
  }

  private handleResume(): void {
    const inactiveAtMs = this.m_inactiveAtMs;
    this.m_inactiveAtMs = null;
    if (
      inactiveAtMs === null ||
      Date.now() - inactiveAtMs < this.m_inactiveThresholdMs
    ) {
      return;
    }

    void this.m_refreshAuthTokens().catch((err: unknown) => {
      console.warn("[AuthWakeRefreshService] token refresh failed:", err);
    });
  }
}
